/**
 * ============================================================
 * SAKHI MASTER — Cart Discount Engine v4
 * ============================================================
 *
 * HOW IT WORKS:
 * ─────────────────────────────────────────────────────────────
 *  1. On every cart change, engine fetches /cart.js
 *  2. Evaluates rules → picks best discount code
 *  3. Updates the "Proceed to Checkout" button URL to:
 *     /discount/FLAT15?redirect=/checkout
 *     This is Shopify's OFFICIAL way to auto-apply a discount.
 *     When the user clicks the button, Shopify sets the session
 *     cookie and redirects to checkout with discount applied.
 *  4. Shows a discount badge in the cart drawer so the user
 *     can clearly see what discount will be applied.
 *
 * NOTE ON IFRAMES:
 *  The iframe /discount/CODE trick is NOT used here. Modern
 *  browsers block third-party cookies set from iframes, making
 *  this approach unreliable. The checkout URL redirect method
 *  is the only officially supported and reliable approach.
 *
 * SETUP IN SHOPIFY ADMIN (required):
 * ─────────────────────────────────────────────────────────────
 *   Admin → Discounts → Create these automatic codes:
 *
 *   Code: FLAT10  | 10% off | No minimum | All products | Unlimited
 *   Code: FLAT15  | 15% off | No minimum | All products | Unlimited
 *   Code: FLAT20  | 20% off | No minimum | All products | Unlimited
 *   Code: FLAT25  | 25% off | No minimum | All products | Unlimited
 *   Code: BULK15  | 15% off | No minimum | All products | Unlimited
 *
 * ============================================================
 */

(function () {
  'use strict';

  // ─── CONFIGURATION ──────────────────────────────────────────
  const CONFIG = {
    // Threshold Discounts — ordered highest to lowest
    // minCartValue is in PAISE (₹1 = 100 paise)
    thresholds: [
      { minCartValue: 349900, discountCode: 'FLAT25', discountPercent: 25 },
      { minCartValue: 249900, discountCode: 'FLAT20', discountPercent: 20 },
      { minCartValue: 149900, discountCode: 'FLAT15', discountPercent: 15 },
      { minCartValue: 99900, discountCode: 'FLAT10', discountPercent: 10 },
    ],

    // Bulk discount: 2+ of the SAME product
    // NOTE: Set enabled:false if you don't have a BULK15 code in your Shopify Admin
    bundle: {
      enabled: false,
      minQuantityPerItem: 2,
      discountCode: 'BULK15',
      discountPercent: 15,
    },

    // Combo bundles (disabled — add product handles to enable)
    comboBundles: [],

    debounceMs: 400,
  };

  // ─── STATE ──────────────────────────────────────────────────
  let _debounceTimer = null;
  let _lock = false;
  let _pendingRecheck = false;

  // ─── PUBLIC API ─────────────────────────────────────────────
  window.CartDiscount = {
    check: _scheduledCheck,
    forceCheck: _runCheck,
    config: CONFIG,
  };

  // ─── DEBOUNCED ENTRY POINT ──────────────────────────────────
  function _scheduledCheck() {
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(_runCheck, CONFIG.debounceMs);
  }

  async function _runCheck() {
    if (_lock) { _pendingRecheck = true; return; }
    _lock = true;
    _pendingRecheck = false;
    try {
      await _evaluate();
    } catch (err) {
      console.warn('[CartDiscount] Error:', err);
    } finally {
      _lock = false;
      if (_pendingRecheck) _scheduledCheck();
    }
  }

  // ─── CORE EVALUATION ───────────────────────────────────────
  async function _evaluate() {
    // 1. Fetch current cart
    let cart;
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      cart = await res.json();
    } catch (err) {
      console.warn('[CartDiscount] Cart fetch failed:', err);
      return;
    }

    // 2. Empty cart → reset everything
    if (!cart.items || cart.items.length === 0) {
      _emit('cart:discount:updated', {
        eligible: false, type: null, percent: 0, code: null,
        cart: cart, eligibleSubtotal: 0,
        nextThreshold: CONFIG.thresholds[CONFIG.thresholds.length - 1],
      });
      return;
    }

    // 3. Calculate eligible subtotal using ORIGINAL (pre-discount) prices
    //    Exclude pre-packaged bundle products from threshold calculation
    let eligibleSubtotal = 0;
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const handle = (item.handle || '').toLowerCase();
      const type   = (item.product_type || '').toLowerCase();
      const title  = (item.product_title || item.title || '').toLowerCase();
      const isBundle = handle.includes('bundle') || type.includes('bundle') || title.includes('bundle');
      if (!isBundle) {
        // Use original_line_price (pre-discount) so existing discount codes
        // don't cause a feedback loop where prices drop below the threshold
        eligibleSubtotal += (item.original_line_price || item.line_price);
      }
    }

    // 4. Evaluate which discount applies — pick highest percent
    let bestType    = null;
    let bestCode    = null;
    let bestPercent = 0;

    // Check BULK (any single item with qty >= 2)
    if (CONFIG.bundle.enabled) {
      for (let i = 0; i < cart.items.length; i++) {
        const item   = cart.items[i];
        const handle = (item.handle || '').toLowerCase();
        const type   = (item.product_type || '').toLowerCase();
        const title  = (item.product_title || item.title || '').toLowerCase();
        if (handle.includes('bundle') || type.includes('bundle') || title.includes('bundle')) continue;
        if (item.quantity >= CONFIG.bundle.minQuantityPerItem) {
          bestType    = 'bundle';
          bestCode    = CONFIG.bundle.discountCode;
          bestPercent = CONFIG.bundle.discountPercent;
          break;
        }
      }
    }

    // Check COMBO BUNDLES
    if (CONFIG.comboBundles && CONFIG.comboBundles.length > 0) {
      const cartHandles = cart.items.map(i => (i.handle || '').toLowerCase());
      for (const combo of CONFIG.comboBundles) {
        const hasAll = combo.requiredHandles.every(h => cartHandles.includes(h.toLowerCase()));
        if (hasAll && combo.discountPercent > bestPercent) {
          bestType    = 'combo';
          bestCode    = combo.discountCode;
          bestPercent = combo.discountPercent;
        }
      }
    }

    // Check THRESHOLDS (sorted highest-first, take first match)
    for (const t of CONFIG.thresholds) {
      if (eligibleSubtotal >= t.minCartValue) {
        if (t.discountPercent > bestPercent) {
          bestType    = 'threshold';
          bestCode    = t.discountCode;
          bestPercent = t.discountPercent;
        }
        break; // thresholds are ordered highest-first
      }
    }

    // 5. Find next threshold the customer hasn't reached yet
    let nextThreshold = null;
    for (let k = CONFIG.thresholds.length - 1; k >= 0; k--) {
      if (eligibleSubtotal < CONFIG.thresholds[k].minCartValue) {
        nextThreshold = CONFIG.thresholds[k];
        break;
      }
    }

    console.info(
      '[CartDiscount] Eligible subtotal: ₹' + (eligibleSubtotal / 100).toFixed(0) +
      ' | Best code: ' + (bestCode || 'none') + ' (' + bestPercent + '%)'
    );

    // 6. Fire event — UI updates checkout button and discount bar immediately
    _emit('cart:discount:updated', {
      eligible: !!bestCode,
      type: bestType,
      percent: bestPercent,
      code: bestCode,
      cart: cart,
      eligibleSubtotal: eligibleSubtotal,
      nextThreshold: nextThreshold,
      bundle: CONFIG.bundle,
    });
  }

  // ─── EVENT EMITTER ─────────────────────────────────────────
  function _emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, cancelable: false }));
  }

  // ─── FETCH INTERCEPTOR (auto-triggers check after cart mutations) ───
  function _interceptCartFetch() {
    const CART_RE = /\/cart\/(add|change|clear)\.js/i;
    const _origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url    = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || 'GET').toUpperCase();
      const result = _origFetch.apply(this, arguments);
      if (method === 'POST' && CART_RE.test(url)) {
        result.then(res => {
          if (res.ok) setTimeout(_scheduledCheck, 100);
        }).catch(() => {});
      }
      return result;
    };
  }

  // ─── INIT ──────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    // Run immediately on page load
    CartDiscount.forceCheck();

    // Listen for explicit cart events from main.js
    ['cart:add', 'cart:update', 'cart:change', 'cart:remove'].forEach(ev => {
      document.addEventListener(ev, _scheduledCheck);
    });

    // Auto-intercept fetch calls to /cart/add.js, /cart/change.js, etc.
    _interceptCartFetch();
  });

})();
